import type {
  AttachedInsight,
  ClaimWithSource,
  ExplanationResult,
  ExtractAndVerifyResponse,
  ExtractResponse,
  FallacyResult,
  VerificationHistoryItem,
  VerifyResponse,
} from "../types/claims";
import type {
  EvidenceMetadataField,
  VerificationEvidenceMetadataResponse,
} from "@shared/types/claims.ts";

import type { Locale } from "@shared/types/locale.ts";
import type { CreateFeedbackReportBody } from "@shared/types/feedback_reports.ts";
import type { OffsetBasedPage } from "@shared/types/pagination.ts";
import { appendOffsetParams, type PageResponse } from "./pagination.ts";
import { claimsAPIClient } from "./apiClients.ts";

export type ExtractInput = { text: string } | { source_url: string };

export async function extractClaims(input: ExtractInput): Promise<ClaimWithSource[]> {
  const { data } = await claimsAPIClient.post<ExtractResponse>("/extract-claims", input);
  return data.claims;
}

/** Collapsed happy path: extract, and verify inline when exactly one claim is found.
 *  Returns `verified` (claim + result, no second round-trip) for the single-claim case, or
 *  `ambiguous` (the claim list) for zero or many claims to disambiguate on the review screen. */
export async function extractAndVerify(input: ExtractInput): Promise<ExtractAndVerifyResponse> {
  const { data } = await claimsAPIClient.post<ExtractAndVerifyResponse>(
    "/extract-and-verify",
    input
  );
  return data;
}

export async function verifyClaim(claimId: string, rawText?: string): Promise<VerifyResponse> {
  const { data } = await claimsAPIClient.post<VerifyResponse>(`/claims/${claimId}/verifications`, {
    raw_text: rawText,
  });
  return data;
}

/**
 * Fetches a verification result pinned to the exact run the user saw.
 * Returns null when the event doesn't exist on the server.
 */
export async function getResult(userEventVerificationId: string): Promise<VerifyResponse | null> {
  const res = await claimsAPIClient.get<VerifyResponse>(
    `/verifications/${userEventVerificationId}`,
    { validateStatus: (s) => (s >= 200 && s < 300) || s === 404 }
  );
  return res.status === 404 ? null : res.data;
}

/** Detect logical fallacies in a verified claim's reasoning. `userEventVerificationId` is the
 *  id returned by verifyClaim/getResult — resolved to the canonical verification internally. */
export async function fetchFallacy(userEventVerificationId: string): Promise<FallacyResult> {
  const { data } = await claimsAPIClient.post<FallacyResult>(
    `/verifications/${userEventVerificationId}/fallacies`
  );
  return data;
}

/** Generate the natural-language explanation paragraph for a verified claim.
 *  Requires fetchFallacy to have completed for this verification first;
 *  otherwise the server returns 409. `userEventVerificationId` is the id returned by
 *  verifyClaim/getResult. `locale` is the reader's locale. */
export async function fetchExplanation(
  userEventVerificationId: string,
  locale: Locale
): Promise<ExplanationResult> {
  const { data } = await claimsAPIClient.post<ExplanationResult>(
    `/verifications/${userEventVerificationId}/explanations`,
    { locale }
  );
  return data;
}

/** Fetches the expert insights attached to a verification, in ranked order.
 *  Returns an empty array when no insights were attached. */
export async function fetchInsights(userEventVerificationId: string): Promise<AttachedInsight[]> {
  const { data } = await claimsAPIClient.get<AttachedInsight[]>(
    `/verifications/${userEventVerificationId}/insights`
  );
  return data;
}

/** Fetches display-only metadata for the evidence a verification used, in the verification's own
 *  order. Reads every readable field when none is named. */
export async function fetchEvidenceMetadata(
  userEventVerificationId: string,
  fields: EvidenceMetadataField[] = []
): Promise<VerificationEvidenceMetadataResponse> {
  const { data } = await claimsAPIClient.get<VerificationEvidenceMetadataResponse>(
    `/verifications/${userEventVerificationId}/evidence-metadata`,
    { params: fields.length > 0 ? { fields: fields.join(",") } : undefined }
  );
  return data;
}

export async function submitFeedback(
  claimId: string,
  rating: number,
  feedbackData: Record<string, unknown>
): Promise<void> {
  await claimsAPIClient.post<{ ok: boolean }>(`/claims/${claimId}/feedback`, {
    rating,
    data: feedbackData,
  });
}

export type PlatformEventType = "share";

export async function trackEvent(
  eventType: PlatformEventType,
  claimId?: string,
  payload?: Record<string, unknown>
): Promise<void> {
  await claimsAPIClient.post<{ ok: boolean }>("/events", {
    event_type: eventType,
    claim_id: claimId,
    payload,
  });
}

/** Ask to be told when the claim of a verification that left it a coverage gap becomes answerable. */
export async function submitCoverageRequest(
  userEventVerificationId: string,
  email: string
): Promise<void> {
  await claimsAPIClient.post<{ ok: boolean }>(
    `/verifications/${userEventVerificationId}/coverage-requests`,
    { email }
  );
}

/** Ask to be told when a claim we do not verify, such as one outside health, becomes one we check. */
export async function submitClaimCoverageRequest(claimId: string, email: string): Promise<void> {
  await claimsAPIClient.post<{ ok: boolean }>(`/claims/${claimId}/coverage-requests`, { email });
}

/** One page of the signed-in person's verification history, newest first. */
export async function listVerificationHistory(
  page: OffsetBasedPage
): Promise<PageResponse<VerificationHistoryItem>> {
  const qs = new URLSearchParams();
  appendOffsetParams(qs, page);
  const { data } = await claimsAPIClient.get<PageResponse<VerificationHistoryItem>>(
    `/verifications?${qs.toString()}`
  );
  return data;
}

/** Sends a bug report, idea or other feedback. Sending the same report (same id) again is harmless. */
export async function sendFeedbackReport(report: CreateFeedbackReportBody): Promise<void> {
  await claimsAPIClient.post("/feedback-reports", report);
}
